import { useState, useEffect, useCallback, useRef } from 'react';
import DocsSidebar from './DocsSidebar';
import DocsHero from './sections/DocsHero';
import DocsInstallation from './sections/DocsInstallation';
import DocsQuickStart from './sections/DocsQuickStart';
import DocsGPUModule from './sections/DocsGPUModule';
import DocsDataModule from './sections/DocsDataModule';
import DocsSignalsModule from './sections/DocsSignalsModule';
import DocsBacktestModule from './sections/DocsBacktestModule';
import DocsFactorsModule from './sections/DocsFactorsModule';
import DocsVizModule from './sections/DocsVizModule';
import DocsEnvModule from './sections/DocsEnvModule';
import DocsConfigModule from './sections/DocsConfigModule';
import DocsExceptionsModule from './sections/DocsExceptionsModule';
import DocsSweepsModule from './sections/DocsSweepsModule';
import DocsFeedsModule from './sections/DocsFeedsModule';
import DocsCLIModule from './sections/DocsCLIModule';

const NAV_OFFSET = 96;

export default function DocsContent() {
  const [activeSection, setActiveSection] = useState('hero');
  const isScrollingRef = useRef(false);

  // Scroll-spy via IntersectionObserver
  useEffect(() => {
    const sections = document.querySelectorAll<HTMLElement>('[data-docs-section]');
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingRef.current) return;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-docs-section');
            if (id) setActiveSection(id);
          }
        }
      },
      { rootMargin: '-20% 0px -75% 0px' }
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Hash navigation on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      requestAnimationFrame(() => {
        scrollToSection(hash);
      });
    }
  }, []);

  const scrollToSection = useCallback((id: string) => {
    // Try data-docs-section first, then id attribute
    const el =
      document.querySelector<HTMLElement>(`[data-docs-section="${id}"]`) ||
      document.getElementById(id);

    if (el) {
      isScrollingRef.current = true;
      setActiveSection(id);

      const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });

      // Update URL hash without scrolling
      window.history.replaceState(null, '', `#${id}`);

      // Re-enable scroll-spy after animation
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  }, []);

  return (
    <div className="container mx-auto px-6 py-12">
      <div className="flex gap-8">
        <DocsSidebar activeSection={activeSection} onNavigate={scrollToSection} />

        <main className="flex-1 min-w-0 max-w-3xl text-left">
          <DocsHero />
          <DocsInstallation />
          <DocsQuickStart />
          <DocsGPUModule />
          <DocsDataModule />
          <DocsSignalsModule />
          <DocsBacktestModule />
          <DocsFactorsModule />
          <DocsVizModule />
          <DocsEnvModule />
          <DocsConfigModule />
          <DocsExceptionsModule />
          <DocsSweepsModule />
          <DocsFeedsModule />
          <DocsCLIModule />
        </main>
      </div>
    </div>
  );
}
